/* The Jobs table: sorted by any column, a page at a time (owner, 2026-10-09: "sorting is missing in
 * table view ... specifically useful to sort based on deadline ... careful with paging as data grows").
 *
 * Two ways, one look. The list the other views share (up to the gateway's 500) is sorted and paged
 * here, so every filter chip and the search keep working instantly. When the list is longer than
 * that, the table asks the gateway for each page instead -- sorted and searched there, so nothing
 * past the 500th row is out of reach; the chips that need every row (needs me, new comments, as a
 * role) then say plainly they work on the shorter lists.
 */
import { useEffect, useMemo, useState } from 'react'
import type { ErpError } from '../api'
import { useErp } from '../context'
import { formatDate } from '../format'
import type { PacksActive, WorkFilters, WorkPage, WorkRow, WorkScope } from './types'
import { usePersonName } from './Pickers'
import { findKind, priorityOf, roleWord, sortRows, stateLabel, stateTone, whoWord, type TableSort } from './words'

const SIZES = [25, 50, 100] as const
const PREF = 'erp.work.table'

type Order = { sort: TableSort; dir: 'asc' | 'desc'; size: number }

function readOrder(): Order {
  try {
    const raw = localStorage.getItem(PREF)
    if (raw) return { sort: 'deadline', dir: 'asc', size: 25, ...(JSON.parse(raw) as Partial<Order>) }
  } catch { /* the defaults */ }
  return { sort: 'deadline', dir: 'asc', size: 25 }
}

const COLUMNS: [TableSort, string][] = [
  ['task', 'Task'], ['state', 'State'], ['assignee', 'Assigned to'], ['raised_by', 'Raised by'], ['start', 'Start'],
  ['deadline', 'Deadline'], ['project', 'Project'], ['updated', 'Updated'],
]

export function WorkTable({ rows, active, onOpen, server }: {
  /** The filtered rows the other views show (the shared list). */
  rows: WorkRow[]
  active: PacksActive
  onOpen: (name: string) => void
  /** Set when the shared list was cut short: each page then comes from the gateway. */
  server?: { scope: WorkScope; closed: boolean; q: string; overdue: boolean; filters?: WorkFilters } | null
}) {
  const erp = useErp()
  const nameOf = usePersonName()
  const [order, setOrder] = useState<Order>(readOrder)
  const [start, setStart] = useState(0)
  const [page, setPage] = useState<WorkPage | null>(null)
  const [failed, setFailed] = useState<string | null>(null)

  const label = (r: WorkRow) => {
    const kind = findKind(active, r.jd_kind)?.kind
    return stateLabel(kind && !kind.flow ? (r.status ?? r.workflow_state) : r.workflow_state, kind?.words, kind?.flow)
  }

  // The server is asked once typing pauses, not on every key.
  const wantQ = server?.q.trim() ?? ''
  const [q, setQ] = useState(wantQ)
  useEffect(() => { const id = setTimeout(() => setQ(wantQ), 350); return () => clearTimeout(id) }, [wantQ])

  // Back to the first page whenever what is listed or its order changes.
  const serverKey = server
    ? `${server.scope}|${server.closed}|${q}|${server.overdue}|${JSON.stringify(server.filters ?? {})}` : ''
  useEffect(() => { setStart(0) }, [order.sort, order.dir, order.size, serverKey, rows])

  useEffect(() => {
    if (!server) { setPage(null); return }
    const controller = new AbortController()
    setFailed(null)
    erp.workPage({ scope: server.scope, closed: server.closed, q, overdue: server.overdue, filters: server.filters,
                   sort: order.sort, dir: order.dir, start, limit: order.size }, controller.signal)
      .then(setPage)
      .catch((e) => { if ((e as Error).name !== 'AbortError') setFailed((e as ErpError).message) })
    return () => controller.abort()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [erp, serverKey, order.sort, order.dir, order.size, start])

  const local = useMemo(() => (server ? [] : sortRows(rows, order.sort, order.dir, label)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, order.sort, order.dir, server, active])
  const shown = server ? (page?.rows ?? []) : local.slice(start, start + order.size)
  const total = server ? page?.page.total ?? null : local.length
  const more = server ? Boolean(page?.page.has_more) : start + order.size < local.length

  function change(next: Partial<Order>) {
    const merged = { ...order, ...next }
    setOrder(merged)
    try { localStorage.setItem(PREF, JSON.stringify(merged)) } catch { /* nothing depends on it */ }
  }
  function sortBy(key: TableSort) {
    change(order.sort === key ? { dir: order.dir === 'asc' ? 'desc' : 'asc' }
      : { sort: key, dir: key === 'updated' ? 'desc' : 'asc' })
  }

  return (
    <div className="card" style={{ overflowX: 'auto' }}>
      {server ? (
        <div className="card-pad small muted" role="note" style={{ paddingBottom: 0 }}>
          A long list: each page is sorted and searched by the server. “Needs me”, “New comments” and the role
          filter work on shorter lists — narrow it by search or by “Mine” first.
        </div>
      ) : null}
      <table className="tbl work-table">
        <thead>
          <tr>
            {COLUMNS.map(([key, text]) => {
              const on = order.sort === key
              return (
                <th key={key} scope="col" aria-sort={on ? (order.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button type="button" className="th-sort" onClick={() => sortBy(key)}
                          aria-label={`Sort by ${text.toLowerCase()}`} title={`Sort by ${text.toLowerCase()}`}>
                    {text}<span aria-hidden="true" className="th-arrow">{on ? (order.dir === 'asc' ? ' ↑' : ' ↓') : ' ↕'}</span>
                  </button>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {failed ? (
            <tr><td colSpan={COLUMNS.length} className="small" style={{ color: 'var(--danger)' }}>{failed}</td></tr>
          ) : server && !page ? (
            <tr><td colSpan={COLUMNS.length} className="small muted">Loading…</td></tr>
          ) : !shown.length ? (
            <tr><td colSpan={COLUMNS.length} className="small muted">Nothing here.</td></tr>
          ) : shown.map((r) => {
            const kind = findKind(active, r.jd_kind)?.kind
            const priority = priorityOf(r, kind)
            const urgent = kind?.fields.find((f) => f.mirror)?.mirror?.values[String(priority)] === 'Urgent'
            const turn = r.needs_me ? `Your turn as ${roleWord(whoWord(r.needs_me, kind?.words))}` : ''
            const state = kind && !kind.flow ? (r.status ?? r.workflow_state) : r.workflow_state
            return (
              <tr key={r.name} tabIndex={0} onClick={() => onOpen(r.name)}
                  onKeyDown={(e) => { if (e.key === 'Enter') onOpen(r.name) }}>
                <td>
                  <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                    <b>{r.subject}</b>
                    {priority ? <span className={`chip ${urgent ? 'chip-danger' : ''}`}>{priority}</span> : null}
                    {r.unseen_comments ? <span className="chip chip-warn" title="New comments">{r.comments} 💬</span> : null}
                    {r.docs ? (
                      <span className={`chip ${r.docs.missing ? 'chip-warn' : 'chip-ok'}`} title="Documents from the client">
                        {r.docs.missing ? `Docs: ${r.docs.missing} missing` : 'Docs in'}
                      </span>
                    ) : null}
                  </div>
                  <div className="small muted">
                    {kind?.label ?? r.jd_kind}{turn ? ` · ${turn}` : ''}{r.jd_round ? ` · revision ${r.jd_round}` : ''}
                  </div>
                </td>
                <td><span className={`chip chip-${stateTone(state)}`}>{label(r)}</span></td>
                <td className="small">{r.assigned.length ? r.assigned.map(nameOf).join(', ') : <span className="muted">—</span>}</td>
                <td className="small">{r.owner ? nameOf(r.owner) : <span className="muted">—</span>}</td>
                <td className="muted">{r.exp_start_date ? formatDate(r.exp_start_date) : '—'}</td>
                <td className={r.overdue ? '' : 'muted'} style={r.overdue ? { color: 'var(--danger)' } : undefined}>
                  {r.exp_end_date ? formatDate(r.exp_end_date) : '—'}{r.overdue ? ' · late' : ''}
                </td>
                <td className="small">
                  {r.project_title ?? r.project ?? ''}
                  {r.customer ? <div className="muted">{r.customer}</div> : null}
                </td>
                <td className="small muted">{r.modified ? formatDate(r.modified) : ''}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <div className="card-pad row row-between small" style={{ flexWrap: 'wrap', gap: 8 }}>
        <span className="muted" aria-live="polite">
          {shown.length ? `${start + 1}–${start + shown.length}` : '0'}{total !== null ? ` of ${total}` : more ? ' of more' : ''}
        </span>
        <div className="row" style={{ gap: 6 }}>
          <label className="row" style={{ gap: 4 }}>
            <span className="muted">Rows</span>
            <select className="input" style={{ width: 'auto', minHeight: 30 }} value={order.size}
                    onChange={(e) => change({ size: Number(e.target.value) })} aria-label="Rows per page">
              {SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <button className="btn btn-sm" disabled={start === 0} onClick={() => setStart(Math.max(0, start - order.size))}>
            ← Previous
          </button>
          <button className="btn btn-sm" disabled={!more} onClick={() => setStart(start + order.size)}>Next →</button>
        </div>
      </div>
    </div>
  )
}
